import type AreaAnalysis from "../AreaAnalysis"
import type Area from "../Area"
import type AnalysisBuilder from "./AnalysisBuilder"

const LOWER_QUARTILE_ENTRY_THRESHOLD = 380_000
const NARROW_SPREAD_THRESHOLD = 100_000
const WIDE_SPREAD_THRESHOLD = 300_000

function moneyValue(m: { v: number | string } | null): number | null {
	if (!m || m.v == null) return null
	return typeof m.v === "number" ? m.v : parseFloat(String(m.v))
}

export class PriceSpreadAnalysisBuilder implements AnalysisBuilder {
	build(analysis: AreaAnalysis, area: Area): string {
		const area_name = area.getDisplayName()
		const parts: string[] = []

		const lowerQuartileSentence = this.lower_quartile_sentence(analysis, area_name)
		if (lowerQuartileSentence) parts.push(lowerQuartileSentence)

		const spreadSentence = this.spread_sentence(analysis, area_name)
		if (spreadSentence) parts.push(spreadSentence)

		return parts.join(" ")
	}

	private lower_quartile_sentence(analysis: AreaAnalysis, area_name: string): string {
		const q1 = moneyValue(analysis.one_year_25th_percentile_price)
		if (q1 == null || q1 >= LOWER_QUARTILE_ENTRY_THRESHOLD) return ""

		return (
			"The lower quartile in " +
			area_name +
			" is around €" +
			this.formatPrice(q1) +
			", highlighting possible market entry points for buyers with tighter budgets and first-step investors."
		)
	}

	private spread_sentence(analysis: AreaAnalysis, area_name: string): string {
		const iqr = moneyValue(analysis.one_year_inter_quartile_range_price)
		if (iqr == null) return ""

		if (iqr < NARROW_SPREAD_THRESHOLD) {
			return (
				"The price spread in " +
				area_name +
				" is relatively narrow, at about €" +
				this.formatPrice(iqr) +
				". Values are clustering closely around the median, which usually points to a more uniform housing stock and steadier buyer expectations."
			)
		}
		if (iqr > WIDE_SPREAD_THRESHOLD) {
			return (
				"The price spread in " +
				area_name +
				" is relatively wide, at about €" +
				this.formatPrice(iqr) +
				". That tends to reflect a broader mix of property types and quality tiers, often creating both premium and value pockets in the same area."
			)
		}
		return (
			"The price spread in " +
			area_name +
			" is in a more typical range at around €" +
			this.formatPrice(iqr) +
			", suggesting a balanced mix of homes without extreme price dispersion."
		)
	}

	private formatPrice(value: number): string {
		if (value >= 1000000) return (value / 1000000).toFixed(3) + "M"
		return value >= 1000 ? (value / 1000).toFixed(0) + "K" : String(value)
	}
}
